import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  FiX,
  FiLayers,
  FiSettings,
  FiShield,
  FiChevronLeft,
  FiChevronRight,
  FiCpu,
  FiCircle,
  FiHexagon,
  FiCopy,
  FiDownload,
  FiUpload
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import TransactionLookupDrawer from '../BlockchainExplorer/TransactionLookupDrawer';
import {
  CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID,
  DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID,
  getDeclaredTokenStandardForContractTemplate,
  listStandardContractTemplates,
  resolveContractArtifact
} from '../../blockchain/standardContractTemplates';
import {
  normalizeTokenStandardKey,
  resolveContractTemplateIdForAssetClass
} from '../../blockchain/resolveMintArtifactForAssetClass';
import { buildSharedSeries721ConstructorParameters } from '../../blockchain/deployConstructorParams';
import {
  MINTED_ASSET_STATUSES,
  TOKENIZABLE_ASSET_STATUSES,
  buildSignerOutputTemplate,
  copyTextToClipboard,
  delay,
  downloadJsonFile,
  formatPreparedPayload,
  isFailedReceiptStatus,
  parseSignedTransactionInput,
  sanitizeContractName
} from '../../blockchain/offlineSigning';
import toast from 'react-hot-toast';
import {
  getOfflineSignerAddressForUser,
  setOfflineSignerAddressForUser
} from '../../api/offlineSignerPreferences';

const WizardContainer = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const WizardTitle = styled.h2`
  font-size: 1.8rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const StepContent = styled(motion.div)`
  min-height: 400px;
`;

const AssetSelector = styled.div`
  margin-bottom: 2rem;

  h3 {
    color: #fff;
    margin-bottom: 1rem;
  }
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const AssetCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid ${props => props.selected ? '#4facfe' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4facfe;
    transform: translateY(-2px);
  }

  .asset-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .asset-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .asset-category {
    color: #4facfe;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .asset-value {
    color: #00ff88;
    font-weight: 600;
    font-size: 1.1rem;
  }

  .asset-description {
    color: #a0a0a0;
    font-size: 0.9rem;
    line-height: 1.4;
    margin-bottom: 1rem;
  }

  .asset-status {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    background: rgba(0, 255, 136, 0.1);
    color: #00ff88;
    border: 1px solid rgba(0, 255, 136, 0.3);
  }
`;

const TokenTypeSelector = styled.div`
  h3 {
    color: #fff;
    margin-bottom: 1.5rem;
  }
`;

const TokenTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const TokenTypeCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid ${props => props.selected ? '#4facfe' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    border-color: #4facfe;
    transform: translateY(-3px);
  }

  .token-icon {
    font-size: 3rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .token-name {
    font-size: 1.3rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .token-standard {
    color: #4facfe;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .token-description {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .token-features {
    display: flex;
    justify-content: space-around;
    font-size: 0.9rem;
  }

  .feature {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;

    .label {
      color: #a0a0a0;
    }

    .value {
      color: ${props => props.divisible ? '#00ff88' : '#ff6b6b'};
      font-weight: 600;
    }
  }
`;

const ConfigurationForm = styled.div`
  h3 {
    color: #fff;
    margin-bottom: 1.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .section-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 500;
    color: #fff;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4facfe;
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  .error {
    color: #ff4757;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .helper {
    color: #a0a0a0;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
`;

const ReviewSection = styled.div`
  h3 {
    color: #fff;
    margin-bottom: 1.5rem;
  }
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;

  .review-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .review-icon {
    font-size: 2rem;
    color: #4facfe;
  }

  .review-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: #fff;
  }

  .review-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .review-item {
    .label {
      color: #a0a0a0;
      font-size: 0.9rem;
      margin-bottom: 0.25rem;
    }

    .value {
      color: #fff;
      font-weight: 600;
    }
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #fff;

  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }

  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1rem;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }

  textarea {
    width: 100%;
    min-height: 180px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  input {
    display: none;
  }
`;

const FlowSteps = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'confirmed' || status === 'success' || status === 'minted'
      ? 'rgba(0, 255, 136, 0.12)'
      : status === 'failed'
        ? 'rgba(255, 107, 107, 0.12)'
        : 'rgba(79, 172, 254, 0.12)'};
  color: ${({ status }) =>
    status === 'confirmed' || status === 'success' || status === 'minted'
      ? '#00ff88'
      : status === 'failed'
        ? '#ff6b6b'
        : '#4facfe'};
  border: 1px solid currentColor;
`;

const HashButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  padding: 0;
  color: #4facfe;
  font-family: Consolas, Monaco, monospace;
  cursor: pointer;
  text-decoration: underline;
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: #4facfe;
    color: #fff;

    &:hover {
      background: #3d8bfe;
      transform: translateY(-1px);
    }

    &:disabled {
      background: #2a2a3e;
      color: #a0a0a0;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

function TokenizationWizard({ onClose, initialAssetId }) {
  const {
    assets,
    tokenTypes,
    loading,
    getAssetById,
    prepareMintToken,
    submitMintToken,
    getMintedTokenByAssetId
  } = useTokenization();
  const {
    assetClasses,
    getAssetClassById,
    prepareContractDeployment,
    submitContractDeployment,
    loading: assetClassCatalogLoading
  } = useAssetClass();
  const { currentIssuer, session, isAdmin } = useTokenIssuer();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedTokenType, setSelectedTokenType] = useState(null);
  const [configuration, setConfiguration] = useState({
    name: '',
    symbol: '',
    totalSupply: '',
    decimals: 18,
    custodian: '',
    signerAddress: '',
    recipientWallet: '',
    contractName: ''
  });
  const [errors, setErrors] = useState({});
  const [preparedDeployment, setPreparedDeployment] = useState(null);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [signedDeploymentInput, setSignedDeploymentInput] = useState('');
  const [preparedMint, setPreparedMint] = useState(null);
  const [mintStatus, setMintStatus] = useState(null);
  const [signedMintInput, setSignedMintInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inspectedTransactionHash, setInspectedTransactionHash] = useState(null);
  const [contractTemplateId, setContractTemplateId] = useState(DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
  const [customContractAbiJson, setCustomContractAbiJson] = useState('');
  const [customContractBytecode, setCustomContractBytecode] = useState('');
  const [customMintFunctionName, setCustomMintFunctionName] = useState('mint');

  const initialAssetAppliedRef = useRef('');

  const selectedAssetClass = useMemo(
    () => assetClasses.find((assetClass) => assetClass.id === selectedAsset?.assetClassId) || null,
    [assetClasses, selectedAsset]
  );

  const compatibleTokenType = useMemo(() => {
    if (!selectedAssetClass) {
      return null;
    }
    const std = normalizeTokenStandardKey(selectedAssetClass.tokenStandard);
    if (std === 'ERC1155') return 'fractionalNFT';
    if (std === 'ERC20') return 'fungible';
    return 'nonFungible';
  }, [selectedAssetClass]);

  useEffect(() => {
    setContractTemplateId((prev) => {
      if (prev === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
        return prev;
      }
      if (!selectedAssetClass) {
        return DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID;
      }
      return resolveContractTemplateIdForAssetClass(selectedAssetClass);
    });
  }, [selectedAssetClass]);

  useEffect(() => {
    if (!selectedAssetClass?.contractAddress) return;
    setContractTemplateId(resolveContractTemplateIdForAssetClass(selectedAssetClass));
  }, [selectedAssetClass]);

  // Filter approved assets that aren't already tokenized
  const availableAssets = assets.filter(asset => 
    TOKENIZABLE_ASSET_STATUSES.has(asset.status) && !asset.tokenizationId
  );

  const currentUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;

  useEffect(() => {
    if (!currentUserId) return;
    const saved = getOfflineSignerAddressForUser(currentUserId);
    if (!saved) return;
    setConfiguration((prev) => (prev.signerAddress?.trim() ? prev : { ...prev, signerAddress: saved }));
  }, [currentUserId]);

  const signerOutputTemplate = formatPreparedPayload(
    buildSignerOutputTemplate({
      expectedContractAddress: preparedDeployment ? '0xExpectedContractAddress' : null
    })
  );

  const resetOfflineFlow = () => {
    setPreparedDeployment(null);
    setDeploymentStatus(null);
    setSignedDeploymentInput('');
    setPreparedMint(null);
    setMintStatus(null);
    setSignedMintInput('');
  };

  useEffect(() => {
    if (!initialAssetId) {
      initialAssetAppliedRef.current = '';
      return;
    }
    if (initialAssetAppliedRef.current === initialAssetId) return;
    if (loading || assetClassCatalogLoading) return;

    const asset = assets.find((a) => String(a.id) === String(initialAssetId));
    if (!asset) {
      if (!loading) {
        initialAssetAppliedRef.current = initialAssetId;
        toast.error('Asset not found in your registry.');
      }
      return;
    }
    if (!TOKENIZABLE_ASSET_STATUSES.has(asset.status) || asset.tokenizationId) {
      initialAssetAppliedRef.current = initialAssetId;
      toast.error('This asset is not available for tokenization.');
      return;
    }

    const ac = assetClasses.find((e) => e.id === asset.assetClassId);
    initialAssetAppliedRef.current = initialAssetId;
    setSelectedAsset(asset);
    resetOfflineFlow();
    const std = normalizeTokenStandardKey(ac?.tokenStandard);
    setSelectedTokenType(
      std === 'ERC1155' ? 'fractionalNFT' : std === 'ERC20' ? 'fungible' : 'nonFungible'
    );
    setConfiguration((previous) => ({
      ...previous,
      name: previous.name || `${asset.name} Token`,
      symbol: previous.symbol || asset.assetNumber?.slice(0, 6)?.toUpperCase() || 'ASSET',
      custodian: previous.custodian || asset.custodian || '',
      recipientWallet: asset.ownerAddress || previous.recipientWallet || '',
      contractName:
        previous.contractName || sanitizeContractName(`${ac?.name || asset.name}NFT`)
    }));
    setCurrentStep(2);
  }, [initialAssetId, assets, assetClasses, loading, assetClassCatalogLoading]);

  const handleCopyPayload = async (payload) => {
    await copyTextToClipboard(formatPreparedPayload(payload));
    toast.success('Copied JSON payload');
  };

  const handleCopyTemplate = async (template) => {
    await copyTextToClipboard(template);
    toast.success('Copied signer template');
  };

  const handleDownloadPayload = (filename, payload) => {
    downloadJsonFile(filename, payload);
    toast.success(`Downloaded ${filename}`);
  };

  const handleSignedFileSelected = (event, setSignedText) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedText(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const applySelectedAsset = (asset) => {
    setSelectedAsset(asset);
    resetOfflineFlow();

    const assetClass = assetClasses.find((entry) => entry.id === asset.assetClassId);
    const std = normalizeTokenStandardKey(assetClass?.tokenStandard);
    setSelectedTokenType(
      std === 'ERC1155' ? 'fractionalNFT' : std === 'ERC20' ? 'fungible' : 'nonFungible'
    );
    setConfiguration((previous) => ({
      ...previous,
      name: previous.name || `${asset.name} Token`,
      symbol: previous.symbol || asset.assetNumber?.slice(0, 6)?.toUpperCase() || 'ASSET',
      custodian: previous.custodian || asset.custodian || '',
      recipientWallet: asset.ownerAddress || previous.recipientWallet || '',
      contractName:
        previous.contractName ||
        sanitizeContractName(`${assetClass?.name || asset.name}NFT`)
    }));
  };

  const pollForDeploymentConfirmation = async (assetClassId, transactionHash) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const refreshedAssetClass = await getAssetClassById(assetClassId, true);
      if (refreshedAssetClass?.contractAddress) {
        return refreshedAssetClass;
      }

      if (transactionHash) {
        try {
          const status =
            deploymentStatus?.deploymentTransactionHash === transactionHash
              ? deploymentStatus?.receiptStatus
              : null;
          if (isFailedReceiptStatus(status)) {
            throw new Error('Deployment transaction failed on chain.');
          }
        } catch (error) {
          throw error;
        }
      }

      await delay(5000);
    }

    return getAssetClassById(assetClassId, true);
  };

  const pollForMintCompletion = async (assetId) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const mintedToken = await getMintedTokenByAssetId(assetId, {
        tokenType: selectedTokenType,
        name: configuration.name,
        symbol: configuration.symbol
      });

      if (mintedToken) {
        return mintedToken;
      }

      const refreshedAsset = await getAssetById(assetId, true);
      if (MINTED_ASSET_STATUSES.has(refreshedAsset?.status)) {
        return getMintedTokenByAssetId(assetId, {
          tokenType: selectedTokenType,
          name: configuration.name,
          symbol: configuration.symbol
        });
      }

      await delay(5000);
    }

    return null;
  };



  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!selectedAsset) newErrors.asset = 'Please select an asset to tokenize';
        break;
      case 2:
        if (!selectedTokenType) newErrors.tokenType = 'Please select a token type';
        if (compatibleTokenType && selectedTokenType !== compatibleTokenType) {
          newErrors.tokenType = `This asset class currently supports ${tokenTypes[compatibleTokenType]?.name || compatibleTokenType}.`;
        }
        break;
      case 3:
        if (!configuration.name) newErrors.name = 'Token name is required';
        if (!configuration.symbol) newErrors.symbol = 'Token symbol is required';
        if (!configuration.signerAddress) newErrors.signerAddress = 'Offline signer address is required';
        if (!configuration.recipientWallet) newErrors.recipientWallet = 'Recipient wallet is required';
        if (selectedTokenType === 'fungible' && (!configuration.totalSupply || Number(configuration.totalSupply) <= 0)) {
          newErrors.totalSupply =
            'Enter the mint amount as a positive integer string (smallest units, e.g. wei for 18 decimals).';
        }
        if (
          selectedTokenType === 'fractionalNFT' &&
          normalizeTokenStandardKey(selectedAssetClass?.tokenStandard) === 'ERC1155' &&
          (!String(configuration.totalSupply || '').trim() || Number(configuration.totalSupply) <= 0)
        ) {
          newErrors.totalSupply = 'Enter a positive ERC-1155 mint amount for this asset.';
        }
        if (contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
          try {
            resolveContractArtifact(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID, {
              abiJson: customContractAbiJson,
              bytecode: customContractBytecode,
              mintFunctionName: customMintFunctionName
            });
          } catch (e) {
            newErrors.customContract = e.message;
          }
          if (!String(customMintFunctionName || '').trim()) {
            newErrors.customMintFn = 'Mint function name is required for your contract.';
          }
          if (!selectedAssetClass?.contractAddress) {
            const bc = String(customContractBytecode || '').trim();
            if (!bc.startsWith('0x') || bc.length < 4) {
              newErrors.customBytecode =
                'Creation bytecode (0x…) is required to deploy your custom contract for this class.';
            }
          }
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    if (!selectedAsset || !selectedAssetClass) {
      toast.error('Selected asset must belong to a backend asset class.');
      return;
    }

    if (!currentUserId) {
      toast.error('Please sign in before preparing transactions.');
      return;
    }

    try {
      setIsSubmitting(true);

      let contractArtifact;
      try {
        contractArtifact = resolveContractArtifact(contractTemplateId, {
          abiJson: customContractAbiJson,
          bytecode: customContractBytecode,
          mintFunctionName: customMintFunctionName
        });
      } catch (e) {
        toast.error(e.message || 'Invalid contract template settings');
        return;
      }
      if (!selectedAssetClass.contractAddress) {
        const bc = String(contractArtifact.bytecode || '').trim();
        if (!bc.startsWith('0x') || bc.length < 4) {
          toast.error('Provide creation bytecode (0x…) to deploy, or use a class that already has a contract address.');
          return;
        }
      }

      let activeAssetClass = selectedAssetClass;
      const contractName = sanitizeContractName(
        configuration.contractName || `${selectedAssetClass.name}NFT`
      );

      if (!activeAssetClass.contractAddress) {
        if (!preparedDeployment) {
          const constructorParameters = await buildSharedSeries721ConstructorParameters({
            contractTemplateId,
            isAdmin: Boolean(isAdmin),
            isPlatformStandard: Boolean(activeAssetClass.isPlatformStandard),
            assetClass: activeAssetClass,
            contractName,
            tokenizerWalletAddresses: [currentIssuer?.walletAddress, configuration.signerAddress]
              .map((w) => String(w || '').trim())
              .filter(Boolean)
          });
          const deploymentPayload = await prepareContractDeployment(activeAssetClass.id, {
            assetClassId: activeAssetClass.id,
            contractName,
            signerAddress: configuration.signerAddress,
            contractAbi: contractArtifact.abiJson,
            bytecode: contractArtifact.bytecode,
            constructorParameters
          });

          setPreparedDeployment(deploymentPayload);
          toast.success('Unsigned deployment transaction ready. Sign offline, then upload or paste the signed raw transaction.');
          return;
        }

        const signedDeployment = parseSignedTransactionInput(signedDeploymentInput);
        const declaredTokenStandard = getDeclaredTokenStandardForContractTemplate(contractTemplateId);
        const deploymentResponse = await submitContractDeployment(activeAssetClass.id, {
          assetClassId: activeAssetClass.id,
          contractName,
          signedTransactionHex: signedDeployment.signedTransactionHex,
          expectedContractAddress: signedDeployment.expectedContractAddress,
          contractAbi: contractArtifact.abiJson,
          bytecode: null,
          constructorParameters: null,
          ...(declaredTokenStandard ? { declaredTokenStandard } : {})
        });

        setDeploymentStatus(deploymentResponse);
        const refreshedAssetClass = await pollForDeploymentConfirmation(
          activeAssetClass.id,
          deploymentResponse.deploymentTransactionHash
        );

        if (!refreshedAssetClass?.contractAddress) {
          throw new Error('Contract submission succeeded, but the deployed address is not available yet.');
        }

        activeAssetClass = refreshedAssetClass;
        setPreparedDeployment(null);
        setSignedDeploymentInput('');
        toast.success(`Contract submitted: ${deploymentResponse.deploymentTransactionHash}`);
      }

      if (!preparedMint) {
        const tsMint = normalizeTokenStandardKey(activeAssetClass.tokenStandard);
        const baseMint = {
          signerAddress: configuration.signerAddress,
          ownerWallet: configuration.recipientWallet,
          contractAddress: activeAssetClass.contractAddress,
          contractAbi: contractArtifact.abiJson,
          functionName: contractArtifact.mintFunctionName
        };
        let preparedMintPayload;
        if (tsMint === 'ERC20') {
          const amt =
            String(configuration.totalSupply || '').trim() || '1000000000000000000';
          preparedMintPayload = await prepareMintToken(selectedAsset.id, {
            ...baseMint,
            tokenId: null,
            amount: amt,
            tokenStandard: 'ERC20'
          });
        } else if (tsMint === 'ERC1155') {
          const amt = String(configuration.totalSupply || '').trim() || '1';
          preparedMintPayload = await prepareMintToken(selectedAsset.id, {
            ...baseMint,
            amount: amt,
            tokenStandard: 'ERC1155'
          });
        } else {
          preparedMintPayload = await prepareMintToken(selectedAsset.id, baseMint);
        }

        setPreparedMint(preparedMintPayload);
        toast.success('Unsigned mint transaction ready. Sign offline, then upload or paste the signed raw transaction.');
        return;
      }

      const signedMint = parseSignedTransactionInput(signedMintInput);
      const tsSubmit = normalizeTokenStandardKey(activeAssetClass.tokenStandard);
      let mintRequest;
      if (tsSubmit === 'ERC721') {
        const resolvedMintId = preparedMint?.resolvedTokenId ?? preparedMint?.ResolvedTokenId;
        if (!resolvedMintId) {
          throw new Error('Missing resolved token id from prepared mint. Run prepare again.');
        }
        mintRequest = await submitMintToken(selectedAsset.id, {
          signedTransactionHex: signedMint.signedTransactionHex,
          tokenId: String(resolvedMintId),
          ownerWallet: configuration.recipientWallet,
          contractAddress: activeAssetClass.contractAddress,
          contractAbi: contractArtifact.abiJson,
          functionName: contractArtifact.mintFunctionName
        });
      } else if (tsSubmit === 'ERC20') {
        const amt =
          String(configuration.totalSupply || '').trim() || '1000000000000000000';
        mintRequest = await submitMintToken(selectedAsset.id, {
          signedTransactionHex: signedMint.signedTransactionHex,
          tokenId: '0',
          amount: amt,
          ownerWallet: configuration.recipientWallet,
          contractAddress: activeAssetClass.contractAddress,
          contractAbi: contractArtifact.abiJson,
          functionName: contractArtifact.mintFunctionName,
          tokenStandard: 'ERC20'
        });
      } else if (tsSubmit === 'ERC1155') {
        const resolvedMintId = preparedMint?.resolvedTokenId ?? preparedMint?.ResolvedTokenId;
        const amt = String(configuration.totalSupply || '').trim() || '1';
        if (!resolvedMintId) {
          throw new Error('Missing resolved ERC-1155 token type id from prepared mint. Run prepare again.');
        }
        mintRequest = await submitMintToken(selectedAsset.id, {
          signedTransactionHex: signedMint.signedTransactionHex,
          tokenId: String(resolvedMintId),
          amount: amt,
          ownerWallet: configuration.recipientWallet,
          contractAddress: activeAssetClass.contractAddress,
          contractAbi: contractArtifact.abiJson,
          functionName: contractArtifact.mintFunctionName,
          tokenStandard: 'ERC1155'
        });
      } else {
        const resolvedMintId = preparedMint?.resolvedTokenId ?? preparedMint?.ResolvedTokenId;
        if (!resolvedMintId) {
          throw new Error('Missing resolved token id from prepared mint. Run prepare again.');
        }
        mintRequest = await submitMintToken(selectedAsset.id, {
          signedTransactionHex: signedMint.signedTransactionHex,
          tokenId: String(resolvedMintId),
          ownerWallet: configuration.recipientWallet,
          contractAddress: activeAssetClass.contractAddress,
          contractAbi: contractArtifact.abiJson,
          functionName: contractArtifact.mintFunctionName
        });
      }

      const mintedToken = await pollForMintCompletion(selectedAsset.id);

      if (!mintedToken) {
        throw new Error('Mint was submitted but confirmation has not arrived yet.');
      }

      setMintStatus({
        requestStatus: mintRequest.status || 'submitted',
        tokenId: mintedToken.tokenId,
        transactionHash: mintedToken.mintTransactionHash,
        ownerWallet: mintedToken.currentOwnerWallet,
        status: 'minted'
      });
      setPreparedMint(null);
      setSignedMintInput('');

      const successLabel =
        tsSubmit === 'ERC20'
          ? `Amount ${mintedToken.amount || configuration.totalSupply || ''} minted`
          : `Token ${mintedToken.tokenId || '—'} minted`;
      toast.success(`Asset tokenized successfully. ${successLabel}.`);
    } catch (error) {
      const msg =
        error.response?.data?.message || error.response?.data?.Message || error.message || 'Failed to tokenize asset';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <AssetSelector>
              <h3>Select Asset to Tokenize</h3>
              <AssetGrid>
                {availableAssets.map(asset => (
                  <AssetCard
                    key={asset.id}
                    selected={selectedAsset?.id === asset.id}
                    onClick={() => applySelectedAsset(asset)}
                  >
                    <div className="asset-header">
                      <div>
                        <div className="asset-name">{asset.name}</div>
                        <div className="asset-category">{asset.category}</div>
                      </div>
                      <div className="asset-value">
                        ${asset.valuationUSD?.toLocaleString()}
                      </div>
                    </div>
                    <div className="asset-description">{asset.description}</div>
                    <div className="asset-status">{asset.status}</div>
                  </AssetCard>
                ))}
              </AssetGrid>
              {availableAssets.length === 0 && (
                <div style={{ color: '#a0a0a0', textAlign: 'center', padding: '2rem' }}>
                  No approved assets are ready for tokenization.
                  <br />
                  Assets must complete legal approval before minting.
                </div>
              )}
              {errors.asset && <div style={{ color: '#ff4757' }}>{errors.asset}</div>}
            </AssetSelector>
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <TokenTypeSelector>
              <h3>Choose Token Type</h3>
              <TokenTypeGrid>
                {Object.values(tokenTypes).map(tokenType => (
                  <TokenTypeCard
                    key={tokenType.id}
                    selected={selectedTokenType === tokenType.id}
                    divisible={tokenType.divisible}
                    onClick={() => setSelectedTokenType(tokenType.id)}
                  >
                    <div className="token-icon">
                      {tokenType.id === 'fungible' && <FiCircle />}
                      {tokenType.id === 'nonFungible' && <FiHexagon />}
                      {tokenType.id === 'fractionalNFT' && <FiLayers />}
                    </div>
                    <div className="token-name">{tokenType.name}</div>
                    <div className="token-standard">{tokenType.standard}</div>
                    <div className="token-description">{tokenType.description}</div>
                    <div className="token-features">
                      <div className="feature">
                        <div className="label">Divisible</div>
                        <div className="value">{tokenType.divisible ? 'Yes' : 'No'}</div>
                      </div>
                      <div className="feature">
                        <div className="label">Unique</div>
                        <div className="value">{tokenType.unique ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                  </TokenTypeCard>
                ))}
              </TokenTypeGrid>
              {errors.tokenType && <div style={{ color: '#ff4757' }}>{errors.tokenType}</div>}
            </TokenTypeSelector>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ConfigurationForm>
              <h3>Token Configuration</h3>
              <FormGrid>
                <FormSection>
                  <div className="section-title">
                    <FiCpu />
                    Standard contract template
                  </div>
                  <FormGroup>
                    <label htmlFor="tokenization-contract-template">Contract template *</label>
                    <select
                      id="tokenization-contract-template"
                      value={contractTemplateId}
                      onChange={(e) => setContractTemplateId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(0,0,0,0.35)',
                        color: '#fff'
                      }}
                    >
                      {listStandardContractTemplates().map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.versionLabel})
                        </option>
                      ))}
                    </select>
                    <div className="helper" style={{ marginTop: '0.5rem' }}>
                      {listStandardContractTemplates().find((x) => x.id === contractTemplateId)?.summary ||
                        'Used for first-time class deployment and mint ABI.'}{' '}
                      {contractTemplateId !== CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                        <span style={{ color: '#94a3b8' }}>
                          Preloaded options are optional; use Custom to bring your own Ping deployment.
                        </span>
                      )}
                    </div>
                    {errors.customContract && <div className="error">{errors.customContract}</div>}
                    {errors.customBytecode && <div className="error">{errors.customBytecode}</div>}
                    {errors.customMintFn && <div className="error">{errors.customMintFn}</div>}
                  </FormGroup>

                  {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                    <FormGroup>
                      <label htmlFor="tokenization-custom-abi">Contract ABI (JSON array) *</label>
                      <textarea
                        id="tokenization-custom-abi"
                        rows={6}
                        placeholder='[{"type":"function","name":"mint",...}]'
                        value={customContractAbiJson}
                        onChange={(e) => setCustomContractAbiJson(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.15)',
                          background: 'rgba(0,0,0,0.35)',
                          color: '#e2e8f0',
                          fontFamily: 'Consolas, Monaco, monospace',
                          fontSize: '0.8rem'
                        }}
                      />
                      <div className="helper">
                        Must match the contract already at the class address (mint) or the one you are deploying.
                      </div>
                    </FormGroup>
                  )}

                  {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID &&
                    !selectedAssetClass?.contractAddress && (
                      <FormGroup>
                        <label htmlFor="tokenization-custom-bytecode">Creation bytecode (hex) *</label>
                        <textarea
                          id="tokenization-custom-bytecode"
                          rows={3}
                          placeholder="0x60806040…"
                          value={customContractBytecode}
                          onChange={(e) => setCustomContractBytecode(e.target.value.trim())}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.15)',
                            background: 'rgba(0,0,0,0.35)',
                            color: '#e2e8f0',
                            fontFamily: 'Consolas, Monaco, monospace',
                            fontSize: '0.8rem'
                          }}
                        />
                        <div className="helper">Required only when this asset class has no deployed address yet.</div>
                      </FormGroup>
                    )}

                  {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                    <FormGroup>
                      <label htmlFor="tokenization-custom-mint-fn">Mint function name *</label>
                      <input
                        id="tokenization-custom-mint-fn"
                        type="text"
                        placeholder="mint"
                        value={customMintFunctionName}
                        onChange={(e) => setCustomMintFunctionName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.15)',
                          background: 'rgba(0,0,0,0.35)',
                          color: '#fff'
                        }}
                      />
                    </FormGroup>
                  )}
                </FormSection>

                <FormSection>
                  <div className="section-title">
                    <FiSettings />
                    Basic Settings
                  </div>
                  <FormGroup>
                    <label>Token Name *</label>
                    <input
                      type="text"
                      placeholder="e.g., Gold Certificate Token"
                      value={configuration.name}
                      onChange={(e) => setConfiguration(prev => ({ ...prev, name: e.target.value }))}
                    />
                    {errors.name && <div className="error">{errors.name}</div>}
                  </FormGroup>
                  <FormGroup>
                    <label>Token Symbol *</label>
                    <input
                      type="text"
                      placeholder="e.g., GOLD"
                      value={configuration.symbol}
                      onChange={(e) => setConfiguration(prev => ({ ...prev, symbol: e.target.value.toUpperCase() }))}
                    />
                    {errors.symbol && <div className="error">{errors.symbol}</div>}
                  </FormGroup>
                  {selectedTokenType === 'fungible' && (
                    <>
                      <FormGroup>
                        <label>
                          {normalizeTokenStandardKey(selectedAssetClass?.tokenStandard) === 'ERC20'
                            ? 'Mint amount (smallest units) *'
                            : 'Total Supply *'}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder={
                            normalizeTokenStandardKey(selectedAssetClass?.tokenStandard) === 'ERC20'
                              ? '1000000000000000000'
                              : '1000000'
                          }
                          value={configuration.totalSupply}
                          onChange={(e) => setConfiguration(prev => ({ ...prev, totalSupply: e.target.value }))}
                        />
                        {errors.totalSupply && <div className="error">{errors.totalSupply}</div>}
                        <div className="helper">
                          {normalizeTokenStandardKey(selectedAssetClass?.tokenStandard) === 'ERC20'
                            ? 'Integer string in token smallest units (catalog CBC20 uses 18 decimals unless your custom contract differs).'
                            : 'For commodities, typically represents kg, oz, or units'}
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <label>Decimals</label>
                        <select
                          value={configuration.decimals}
                          onChange={(e) => setConfiguration(prev => ({ ...prev, decimals: Number(e.target.value) }))}
                        >
                          <option value={0}>0 (Whole units only)</option>
                          <option value={2}>2 (Like USD cents)</option>
                          <option value={6}>6 (USDC standard)</option>
                          <option value={18}>18 (ETH standard)</option>
                        </select>
                        <div className="helper">Number of decimal places for fractional units</div>
                      </FormGroup>
                    </>
                  )}
                  {selectedTokenType === 'fractionalNFT' &&
                    normalizeTokenStandardKey(selectedAssetClass?.tokenStandard) === 'ERC1155' && (
                      <FormGroup>
                        <label>ERC-1155 mint amount *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="1"
                          value={configuration.totalSupply}
                          onChange={(e) =>
                            setConfiguration((prev) => ({ ...prev, totalSupply: e.target.value }))
                          }
                        />
                        {errors.totalSupply && <div className="error">{errors.totalSupply}</div>}
                        <div className="helper">Units minted for this asset&apos;s token type (often 1).</div>
                      </FormGroup>
                    )}
                </FormSection>

                <FormSection>
                  <div className="section-title">
                    <FiShield />
                    Custody & Compliance
                  </div>
                  <FormGroup>
                    <label>Offline Signer Address *</label>
                    <input
                      type="text"
                      placeholder="0xSignerAddress"
                      value={configuration.signerAddress}
                      onChange={(e) => {
                        const v = e.target.value;
                        setConfiguration((prev) => ({ ...prev, signerAddress: v }));
                        setOfflineSignerAddressForUser(currentUserId, v);
                      }}
                    />
                    {errors.signerAddress && <div className="error">{errors.signerAddress}</div>}
                    <div className="helper">
                      This must match the address used by the separate offline signer app. Remembered for your account
                      in this browser.
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <label>Recipient Wallet *</label>
                    <input
                      type="text"
                      placeholder="0xRecipientWallet"
                      value={configuration.recipientWallet}
                      onChange={(e) => setConfiguration(prev => ({ ...prev, recipientWallet: e.target.value }))}
                    />
                    {errors.recipientWallet && <div className="error">{errors.recipientWallet}</div>}
                    <div className="helper">The NFT will be minted to this wallet address.</div>
                  </FormGroup>
                  <FormGroup>
                    <label>Custodian</label>
                    <input
                      type="text"
                      placeholder="Entity responsible for asset custody"
                      value={configuration.custodian}
                      onChange={(e) => setConfiguration(prev => ({ ...prev, custodian: e.target.value }))}
                    />
                    <div className="helper">Organization holding the physical asset</div>
                  </FormGroup>
                  <FormGroup>
                    <label>Contract Name</label>
                    <input
                      type="text"
                      placeholder="PingRealEstateNFT"
                      value={configuration.contractName}
                      onChange={(e) => setConfiguration(prev => ({ ...prev, contractName: e.target.value }))}
                    />
                    <div className="helper">Used only if this asset class has not deployed a contract yet.</div>
                  </FormGroup>
                </FormSection>
              </FormGrid>
            </ConfigurationForm>
          </StepContent>
        );

      case 4:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ReviewSection>
              <h3>Review & Deploy</h3>
              
              <ReviewCard>
                <div className="review-header">
                  <div className="review-icon">
                    {selectedTokenType === 'fungible' && <FiCircle />}
                    {selectedTokenType === 'nonFungible' && <FiHexagon />}
                    {selectedTokenType === 'fractionalNFT' && <FiLayers />}
                  </div>
                  <div className="review-title">Tokenization Summary</div>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <div className="label">Asset</div>
                    <div className="value">{selectedAsset?.name}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Token Type</div>
                    <div className="value">{tokenTypes[selectedTokenType]?.name}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Asset Class</div>
                    <div className="value">{selectedAssetClass?.name || 'Not linked'}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Standard</div>
                    <div className="value">{tokenTypes[selectedTokenType]?.standard}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Token Name</div>
                    <div className="value">{configuration.name}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Symbol</div>
                    <div className="value">{configuration.symbol}</div>
                  </div>
                  {selectedTokenType === 'fungible' && (
                    <div className="review-item">
                      <div className="label">Total Supply</div>
                      <div className="value">{Number(configuration.totalSupply).toLocaleString()}</div>
                    </div>
                  )}
                  <div className="review-item">
                    <div className="label">Asset Value</div>
                    <div className="value">${selectedAsset?.valuationUSD?.toLocaleString()}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Custodian</div>
                    <div className="value">{configuration.custodian || 'Not specified'}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Signer Address</div>
                    <div className="value">{configuration.signerAddress}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Recipient Wallet</div>
                    <div className="value">{configuration.recipientWallet}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Contract Address</div>
                    <div className="value">{selectedAssetClass?.contractAddress || 'Will be deployed in this flow'}</div>
                  </div>
                  <div className="review-item">
                    <div className="label">Contract template</div>
                    <div className="value">
                      {listStandardContractTemplates().find((x) => x.id === contractTemplateId)?.name || contractTemplateId}
                    </div>
                  </div>
                </div>
              </ReviewCard>

              <StatusPanel>
                <div className="title">Offline signing &amp; broadcast (Ping chain)</div>
                <FlowSteps>
                  <li>
                    <strong>Unsigned transaction</strong> — Use the button below to create the unsigned deployment (and later
                    mint) transaction via the API. Only fields needed for signing are produced; your keys never leave the
                    offline signer.
                  </li>
                  <li>
                    <strong>Authorized tokenizer signs offline</strong> — Copy or download the JSON, sign with your cold
                    wallet or the Ping offline signer app using the signer address you entered.
                  </li>
                  <li>
                    <strong>Upload or paste signed transaction</strong> — Return to this Core Tokenizer screen, load the
                    signer output file or paste <code>signedTransactionHex</code>. The app submits it to the backend, which
                    broadcasts <code>xcb_sendRawTransaction</code> to the Core blockchain node.
                  </li>
                </FlowSteps>
                <div className="meta">
                  <span>Contract: {selectedAssetClass?.contractAddress ? 'Existing contract' : 'Deploy NFT contract first'}</span>
                  <span>
                    Mint function:{' '}
                    {(() => {
                      try {
                        return resolveContractArtifact(contractTemplateId, {
                          abiJson: customContractAbiJson,
                          bytecode: customContractBytecode,
                          mintFunctionName: customMintFunctionName
                        }).mintFunctionName;
                      } catch {
                        return '—';
                      }
                    })()}
                  </span>
                </div>
              </StatusPanel>

              {deploymentStatus && (
                <StatusPanel>
                  <div className="title">Deployment Submitted</div>
                  <div className="message">
                    Deployment was submitted to the backend and the screen is polling for reconciliation.
                  </div>
                  <div className="meta">
                    <span>
                      Transaction:{' '}
                      <HashButton onClick={() => setInspectedTransactionHash(deploymentStatus.deploymentTransactionHash)}>
                        {deploymentStatus.deploymentTransactionHash}
                      </HashButton>
                    </span>
                    <span>
                      Status:{' '}
                      <StatusBadge status={deploymentStatus.status || 'submitted'}>
                        {deploymentStatus.status || 'submitted'}
                      </StatusBadge>
                    </span>
                  </div>
                </StatusPanel>
              )}

              {preparedDeployment && (
                <>
                  <PayloadBox>
                    <div className="title">Unsigned contract deployment transaction</div>
                    <div className="hint">
                      This is the unsigned transaction for deploying the token contract. Feed it to your offline signer; do
                      not sign in the browser.
                    </div>
                    <PayloadActions>
                      <MiniButton onClick={() => handleCopyPayload(preparedDeployment)}>
                        <FiCopy />
                        Copy JSON
                      </MiniButton>
                      <MiniButton
                        onClick={() =>
                          handleDownloadPayload(
                            `${selectedAssetClass?.code || 'asset-class'}-deploy-prepared.json`,
                            preparedDeployment
                          )
                        }
                      >
                        <FiDownload />
                        Download JSON
                      </MiniButton>
                    </PayloadActions>
                    <textarea readOnly value={formatPreparedPayload(preparedDeployment)} />
                  </PayloadBox>
                  <PayloadBox>
                    <div className="title">Signer Output Template</div>
                    <div className="hint">
                      Paste the signer result in this shape, or paste only `signedTransactionHex`.
                    </div>
                    <PayloadActions>
                      <MiniButton onClick={() => handleCopyTemplate(signerOutputTemplate)}>
                        <FiCopy />
                        Copy Template
                      </MiniButton>
                    </PayloadActions>
                    <textarea readOnly value={signerOutputTemplate} />
                  </PayloadBox>
                  <PayloadBox>
                    <div className="title">Signed deployment — upload or paste</div>
                    <div className="hint">
                      After offline signing, upload a JSON file from your signer or paste the full output. Raw{' '}
                      <code>0x</code>-prefixed <code>signedTransactionHex</code> alone is also accepted. This will be sent
                      to the API to broadcast to the Ping chain.
                    </div>
                    <PayloadActions>
                      <FilePickLabel>
                        <FiUpload aria-hidden />
                        Upload signed file
                        <input
                          type="file"
                          accept=".json,.txt,text/json,application/json"
                          onChange={(e) => handleSignedFileSelected(e, setSignedDeploymentInput)}
                        />
                      </FilePickLabel>
                    </PayloadActions>
                    <textarea
                      value={signedDeploymentInput}
                      onChange={(e) => setSignedDeploymentInput(e.target.value)}
                      placeholder="Paste signedTransactionHex or full signer JSON here…"
                    />
                  </PayloadBox>
                </>
              )}

              {mintStatus && (
                <StatusPanel>
                  <div className="title">Mint Submitted</div>
                  <div className="message">
                    Mint transaction was broadcast via the backend and is being reconciled on the Ping chain.
                  </div>
                  <div className="meta">
                    <span>
                      Request:{' '}
                      <StatusBadge status={mintStatus.requestStatus || 'submitted'}>
                        {mintStatus.requestStatus || 'submitted'}
                      </StatusBadge>
                    </span>
                    <span>
                      Result:{' '}
                      <StatusBadge status={mintStatus.status || 'submitted'}>
                        {mintStatus.status || 'submitted'}
                      </StatusBadge>
                    </span>
                    <span>
                      Mint tx:{' '}
                      {mintStatus.transactionHash ? (
                        <HashButton onClick={() => setInspectedTransactionHash(mintStatus.transactionHash)}>
                          {mintStatus.transactionHash}
                        </HashButton>
                      ) : (
                        'Pending reconciliation'
                      )}
                    </span>
                    <span>Asset: {selectedAsset?.name}</span>
                  </div>
                </StatusPanel>
              )}

              {preparedMint && (
                <>
                  <PayloadBox>
                    <div className="title">Unsigned mint transaction</div>
                    <div className="hint">
                      Unsigned transaction to mint the NFT on the deployed contract. Sign offline, then upload or paste the
                      signed result below for broadcast.
                    </div>
                    <PayloadActions>
                      <MiniButton onClick={() => handleCopyPayload(preparedMint)}>
                        <FiCopy />
                        Copy JSON
                      </MiniButton>
                      <MiniButton
                        onClick={() =>
                          handleDownloadPayload(
                            `${selectedAsset?.assetNumber || selectedAsset?.id || 'asset'}-mint-prepared.json`,
                            preparedMint
                          )
                        }
                      >
                        <FiDownload />
                        Download JSON
                      </MiniButton>
                    </PayloadActions>
                    <textarea readOnly value={formatPreparedPayload(preparedMint)} />
                  </PayloadBox>
                  <PayloadBox>
                    <div className="title">Signer Output Template</div>
                    <div className="hint">
                      Use one signer output object for mint. The important field is `signedTransactionHex`.
                    </div>
                    <PayloadActions>
                      <MiniButton onClick={() => handleCopyTemplate(signerOutputTemplate)}>
                        <FiCopy />
                        Copy Template
                      </MiniButton>
                    </PayloadActions>
                    <textarea readOnly value={signerOutputTemplate} />
                  </PayloadBox>
                  <PayloadBox>
                    <div className="title">Signed mint — upload or paste</div>
                    <div className="hint">
                      Upload the signer output file or paste JSON / raw <code>signedTransactionHex</code>. The Core Tokenizer
                      backend will broadcast it to the Ping blockchain.
                    </div>
                    <PayloadActions>
                      <FilePickLabel>
                        <FiUpload aria-hidden />
                        Upload signed file
                        <input
                          type="file"
                          accept=".json,.txt,text/json,application/json"
                          onChange={(e) => handleSignedFileSelected(e, setSignedMintInput)}
                        />
                      </FilePickLabel>
                    </PayloadActions>
                    <textarea
                      value={signedMintInput}
                      onChange={(e) => setSignedMintInput(e.target.value)}
                      placeholder="Paste signedTransactionHex or full signer JSON here…"
                    />
                  </PayloadBox>
                </>
              )}
            </ReviewSection>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <TransactionLookupDrawer
        transactionHash={inspectedTransactionHash}
        onClose={() => setInspectedTransactionHash(null)}
      />
      <WizardHeader>
        <WizardTitle>
          <FiLayers /> Tokenize Asset
        </WizardTitle>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
      </WizardHeader>

      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      <WizardActions>
        <ActionButton 
          className="secondary" 
          onClick={handlePrevious}
          disabled={currentStep === 1}
        >
          <FiChevronLeft />
          Previous
        </ActionButton>

        {currentStep < 4 ? (
          <ActionButton className="primary" onClick={handleNext}>
            Next
            <FiChevronRight />
          </ActionButton>
        ) : (
          <ActionButton
            className="primary"
            onClick={handleSubmit}
            disabled={
              loading ||
              isSubmitting ||
              mintStatus?.status === 'minted' ||
              (Boolean(preparedDeployment) && !signedDeploymentInput.trim()) ||
              (Boolean(preparedMint) && Boolean(selectedAssetClass?.contractAddress) && !signedMintInput.trim())
            }
          >
            {isSubmitting || loading
              ? 'Processing...'
              : mintStatus?.status === 'minted'
                ? 'Tokenization complete'
                : !selectedAssetClass?.contractAddress && !preparedDeployment
                  ? 'Create unsigned deployment'
                  : preparedDeployment
                    ? 'Broadcast signed deployment'
                    : !preparedMint
                      ? 'Create unsigned mint'
                      : 'Broadcast signed mint'}
            <FiCpu />
          </ActionButton>
        )}
      </WizardActions>
    </WizardContainer>
  );
}

export default TokenizationWizard; 